/* eslint-disable no-extra-parens */
import { createInstance } from './axios.api'
import { getJwt } from './authentication.v6.service'

import { parseSpreadsheetFile } from '@/utils/spreadsheet.utils'

import {
  caseReasonMappedNames,
  ContactFormCaseReason,
  ContactFormCaseReasonTransformed,
  ContactFormProperties,
  ContactFormRequest,
  ParsedAddress
} from '@/types/landlord.types'
import { ParsedSpreadsheet } from '@/types/spreadsheet-utils.types'
import {
  AddressValidationRequest,
  OrderFormFields,
  ParsedAddressSpreadsheetData
} from '@/types/landlord-order.types'

const prepareAuthorizationHeader = (token: string) => ({ headers: { Authorization: `Bearer ${token}` } })

const uploadAttachment = async (file: File, caseId: string, email: string) => {
  const { token } = await getJwt()
  const axios = createInstance(process.env.VUE_APP_SNOW_INTEGRATION_URL)

  const { data } = await axios.put(
    `/file/${caseId}/attachment/${file.name}`,
    file,
    {
      headers: {
        Authorization: `Bearer ${token}`,
        'Content-Type': file.type,
        email
      }
    }
  )

  return data
}

const uploadAttachmentsInSequence = async (files: File[], caseId: string, email: string) => {
  await files.reduce(async (promise, file) => {
    await promise

    try {
      return await uploadAttachment(file, caseId, email)
    } catch {
      throw new Error('Failed to upload attachment')
    }
  }, Promise.resolve())
}

export const submitLandlordOrderForm = async (fields: OrderFormFields): Promise<number> => {
  const { token } = await getJwt()

  const axios = createInstance(process.env.VUE_APP_SNOW_INTEGRATION_URL)
  const { data } = await axios.post('/order/case/create', fields, {
    ...prepareAuthorizationHeader(token)
  })

  return data.result.caseId
}

export const submitLandlordContactForm = async (contactFormRequest: ContactFormRequest): Promise<string> => {
  const { token } = await getJwt()

  const fields = {
    caseReason: contactFormRequest[ContactFormProperties.CaseReason],
    description: contactFormRequest[ContactFormProperties.Description],
    companyCode: contactFormRequest[ContactFormProperties.CompanyCode],
    email: contactFormRequest[ContactFormProperties.EmailAddress],
    phone: contactFormRequest[ContactFormProperties.PhoneNumber],
    preferredContactForm: contactFormRequest[ContactFormProperties.PreferredContactForm],
    koNumber: contactFormRequest[ContactFormProperties.KoNumber]
  }

  const axios = createInstance(process.env.VUE_APP_SNOW_INTEGRATION_URL)
  const { data } = await axios.post('/case/create', fields, {
    ...prepareAuthorizationHeader(token)
  })

  const caseId = data.result.caseId

  await uploadAttachmentsInSequence(
    contactFormRequest[ContactFormProperties.Attachments],
    caseId,
    contactFormRequest[ContactFormProperties.EmailAddress]
  )

  return caseId
}

export const getCaseReasons = async (): Promise<ContactFormCaseReasonTransformed[]> => {
  const { token } = await getJwt()

  const axios = createInstance(process.env.VUE_APP_SNOW_INTEGRATION_URL)
  const { data } = await axios.get('/case-reasons', {
    ...prepareAuthorizationHeader(token)
  })

  return (data as ContactFormCaseReason[]).map(reason => ({
    id: reason.id,
    name: caseReasonMappedNames[reason.id],
    value: reason.id,
    label: caseReasonMappedNames[reason.id]
  }))
}

export const getAuthorizationHeader = async () => {
  if (process.env.VUE_APP_DEVELOPMENT_MODE_BYPASS_BANK_ID === 'true') {
    return null
  }

  const { token } = await getJwt()
  return prepareAuthorizationHeader(token)
}

export const getCompanyName = async (companyCode: string): Promise<string> => {
  const axios = createInstance(process.env.VUE_APP_ALPHA_ONE_SERVICE_URL)

  const { data } = await axios.get(`/${companyCode}`, {
    ...await getAuthorizationHeader()
  })

  return data.companyName
}

export const validateAddress = async (payload: AddressValidationRequest): Promise<string> => {
  const axios = createInstance(process.env.VUE_APP_ADDRESSMASTER_URL)
  const { data } = await axios.post('/address/validate', payload, {
    ...await getAuthorizationHeader()
  })

  return data
}

export const validateAddresses = async (addresses: ParsedAddressSpreadsheetData[]): Promise<ParsedAddressSpreadsheetData[]> => {
  const axios = createInstance(process.env.VUE_APP_ADDRESSMASTER_URL)
  const { data } = await axios.post('/address/validateList', { addresses }, {
    ...await getAuthorizationHeader()
  })

  return data
}

const prepareAddressData = (spreadsheetData: ParsedSpreadsheet): ParsedAddressSpreadsheetData[] => {
  return spreadsheetData.data.map((addressData) => {
    return {
      [ParsedAddress.RowIndex]: addressData.__rowNum__,
      [ParsedAddress.Street]: addressData[ParsedAddress.Street] || '',
      [ParsedAddress.StreetNumber]: addressData[ParsedAddress.StreetNumber] || '',
      [ParsedAddress.City]: addressData[ParsedAddress.City] || '',
      [ParsedAddress.ApartmentNumber]: addressData[ParsedAddress.ApartmentNumber] || '',
      [ParsedAddress.Entrance]: addressData[ParsedAddress.Entrance] || '',
      [ParsedAddress.FloorPlan]: addressData[ParsedAddress.FloorPlan] || '',
      [ParsedAddress.TenantsName]: addressData[ParsedAddress.TenantsName] || '',
      [ParsedAddress.TenantsPhone]: addressData[ParsedAddress.TenantsPhone] || '',
      [ParsedAddress.SwitchLocation]: addressData[ParsedAddress.SwitchLocation] || ''
    } as ParsedAddressSpreadsheetData
  })
}

export const parseExcelSheetAddresses = async (file: File, range: string): Promise<ParsedAddressSpreadsheetData[]> => {
  const headers = [
    ParsedAddress.Street,
    ParsedAddress.StreetNumber,
    ParsedAddress.Entrance,
    ParsedAddress.FloorPlan,
    ParsedAddress.ApartmentNumber,
    ParsedAddress.City,
    ParsedAddress.TenantsName,
    ParsedAddress.TenantsPhone,
    ParsedAddress.SwitchLocation
  ]

  const parsedSpreadsheet = await parseSpreadsheetFile(file, headers, range)

  return prepareAddressData(parsedSpreadsheet)
}
